import * as R from "ramda";
import * as Dfns from "date-fns/fp";
import { METRIC_TO_PRETTY_NAME } from "./crossChannelConstants";
import {
  CURRENCY_FORMATTER_MAP,
  formatMixedCurrency,
  formatMoney,
  formatNumber,
  formatNumberAsInt,
} from "../utils/format-utils";
import { PerformanceData } from "@blisspointmedia/bpm-types/dist/MetricsTable";
import { CURRENCY_SYMBOL_MAP } from "@blisspointmedia/bpm-types/dist/CurrencySymbols";

export type DateGroupingKey = "Day" | "Week" | "Month" | "Quarter" | "Year";

interface DateGroupingOption {
  value: DateGroupingKey;
  label: string;
}
export const DATE_GROUPING_OPTIONS: DateGroupingOption[] = [
  { value: "Day", label: "Daily" },
  { value: "Week", label: "Weekly" },
  { value: "Month", label: "Monthly" },
  { value: "Quarter", label: "Quarterly" },
  { value: "Year", label: "Yearly" },
];

/**
 * Return the date grouping options that are relevant based on the provided date range.
 * For example, if the date range spans 3 weeks we don't want to provide the option to group by
 * month, quarter, or year.
 */
export const getDateGroupingOptions = (start: string, end: string): DateGroupingOption[] => {
  const DATE_GROUPING_TO_DAYS = {
    Day: 1,
    Week: 7,
    Month: 31,
    Quarter: 89,
    Year: 366,
  };

  const daysDiff = Dfns.differenceInDays(Dfns.parseISO(start), Dfns.parseISO(end));
  return DATE_GROUPING_OPTIONS.filter(option => daysDiff > DATE_GROUPING_TO_DAYS[option.value]);
};

// Metric formatters
export const VOLUME_FORMATTER = (value: number): string =>
  value > 0 && value < 1 ? "<1" : formatNumberAsInt(value);

export const ROAS_FORMATTER = (value: number): string =>
  value > 0 && value < 0.01 ? "<0.01" : formatNumber(value, 2);

export const CPX_FORMATTER = (value: number): string =>
  value > 0 && value < 0.01 ? "<$0.01" : formatMoney(value, value < 15 ? 2 : 0);

export const CPM_FORMATTER = (value: number): string => formatMoney(value, 2);

export const IMP_FORMATTER = (value: number): string => formatNumberAsInt(value);

export const SPEND_FORMATTER = (value: number): string =>
  value > 0 && value < 1 ? "<$1" : formatMoney(value, 0);

export const REVENUE_FORMATTER = (value: number): string =>
  value > 0 && value < 1 ? "<$1" : formatMoney(value, 0);

export const RELATIVE_FORMATTER = (value: number): string =>
  `${Math.round(value).toLocaleString()}%`;

export const PERCENTAGE_FORMATTER = (value: number): string =>
  `${formatNumber(value * 100, 0).toLocaleString()}%`;

export const SPEND_IN_THOUSANDS_FORMATTER = (value: number): string =>
  value > 0 && value < 1 ? "<$1" : formatMoney(value / 1000, 0);

export const IMP_IN_THOUSANDS_FORMATTER = (value: number): string =>
  formatNumberAsInt(value / 1000);

export const FOREIGN_SPEND_OR_REVENUE_FORMATTER = (value: number, currCode: string): string => {
  const symbol = CURRENCY_SYMBOL_MAP[currCode] || "mixed ";
  const formatter = CURRENCY_FORMATTER_MAP[currCode] || formatMixedCurrency;
  return value > 0 && value < 1 ? `<${symbol}1` : formatter(value, 0);
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const NATIVE_SPEND_OR_REVENUE_FORMATTER = (value: number, _d, _z, _m, _r, currCode: string): string => {
  if (!currCode || currCode === "" || currCode === "USD") {
    return SPEND_FORMATTER(value);
  } else {
    return FOREIGN_SPEND_OR_REVENUE_FORMATTER(value, currCode);
  }
};

export const FOREIGN_CPM_FORMATTER = (value: number, currCode: string): string => {
  const formatter = CURRENCY_FORMATTER_MAP[currCode] || formatMixedCurrency;
  return formatter(value, 2);
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const NATIVE_CPM_FORMATTER = (value: number, _d, _z, _m, _r, currCode: string): string => {
  if (!currCode || currCode === "" || currCode === "USD") {
    return CPM_FORMATTER(value);
  } else {
    return FOREIGN_CPM_FORMATTER(value, currCode);
  }
};

export const FOREIGN_CPX_FORMATTER = (value: number, currCode: string): string => {
  const symbol = CURRENCY_SYMBOL_MAP[currCode] || "mixed ";
  const formatter = CURRENCY_FORMATTER_MAP[currCode] || formatMixedCurrency;
  return value > 0 && value < 0.01 ? `<${symbol}0.01` : formatter(value, value < 15 ? 2 : 0);
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const NATIVE_CPX_FORMATTER = (value: number, _d, _z, _m, _r, currCode: string): string => {
  if (!currCode || currCode === "" || currCode === "USD") {
    return CPX_FORMATTER(value);
  } else {
    return FOREIGN_CPX_FORMATTER(value, currCode);
  }
};

// formatValue receives parameters from metricsTableUtils.ts which we are utilizing here to determine special formatting for Streaming Video.
export const CLICKS_FORMATTER = (
  value: number,
  decimals: number,
  zeroLabel: string | number,
  min: number,
  row: PerformanceData
): string => {
  if (
    ["Linear", "Streaming Video", "Video"].includes(`${R.path(["dimensions", "Channel"], row)}`) &&
    value < 1
  ) {
    return "--";
  }
  return value > 0 && value < 1 ? "<1" : formatNumberAsInt(value);
};

export const METRIC_TO_FORMATTER = {
  clicks: CLICKS_FORMATTER,
  cpc: CPM_FORMATTER,
  cpm: CPM_FORMATTER,
  cpx: CPX_FORMATTER,
  imps: IMP_FORMATTER,
  percentage: PERCENTAGE_FORMATTER,
  relative: RELATIVE_FORMATTER,
  revenue: REVENUE_FORMATTER,
  roas: ROAS_FORMATTER,
  spend: SPEND_FORMATTER,
  volume: VOLUME_FORMATTER,
  spend_native: NATIVE_SPEND_OR_REVENUE_FORMATTER,
  revenue_native: NATIVE_SPEND_OR_REVENUE_FORMATTER,
  cpmNative: NATIVE_CPM_FORMATTER,
  cpcNative: NATIVE_CPM_FORMATTER,
  cpxNative: NATIVE_CPX_FORMATTER,
};

/**
 * Returns the formatter function for the provided metric.
 */
export const metricFormatter = (metric: string): ((value: number) => string) => {
  if (METRIC_TO_FORMATTER[metric]) {
    return METRIC_TO_FORMATTER[metric];
  } else {
    return METRIC_TO_FORMATTER.volume;
  }
};

/**
 * Returns the pretty name for the provided metric.
 */
export const getPrettyMetricName = (metric: string): string => {
  return METRIC_TO_PRETTY_NAME[metric] || metric;
};
